import type { CSSProperties } from "react";
import { CameraIcon } from "./icons";

export interface ImagePreviewProps {
  /** Image URL. When null/undefined, the muted camera-icon placeholder is shown instead. */
  src?: string | null;
  /** Alt text for the populated image (also the accessible name). */
  alt: string;
  /** Square frame edge length as a CSS length (default "9.8rem"), applied via `--fui-image-preview-size`. */
  size?: string;
  className?: string;
}

/**
 * A square image-preview frame — a cover-fit `<img>` when `src` is set, otherwise a muted
 * camera-icon placeholder. Presentational only: no upload or data fetching; the host supplies the URL.
 *
 * Extracted from the animal-detail "Basisinformatie" hero photo block. Visual ground truth is the
 * Jmix `animal-card` photo; styles live in ui.css, token-driven. The frame size is overridable via
 * the `size` prop (a CSS length passed through the `--fui-image-preview-size` custom property).
 */
export function ImagePreview({ src, alt, size, className }: ImagePreviewProps) {
  const classes = ["fui-image-preview"];
  if (className) classes.push(className);
  const style = size ? ({ "--fui-image-preview-size": size } as CSSProperties) : undefined;

  return (
    <div className={classes.join(" ")} style={style}>
      {src ? (
        <img className="fui-image-preview__img" src={src} alt={alt} />
      ) : (
        <span className="fui-image-preview__empty" aria-hidden="true">
          <CameraIcon width={38} height={38} strokeWidth={1.5} />
        </span>
      )}
    </div>
  );
}
